'use client';

import { useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';
import { ErrorBoundary } from '@benzinga/core-ui';
import { menus, NavigationHeader, NavigationHeaderProps } from '@benzinga/navigation-ui';
import { resolveHubNavEnabled } from '../../_libs/site-shell/resolveHubNav';
import { usePageLocale } from './pageLocale';
import { disableOptinMonsterCampaigns, isArticlePath, isAdLightDomHintActive } from '@benzinga/ads-utils';
import { useBenzingaEdge } from '@benzinga/edge';
import { HeaderProps } from '../../../src/entities/header';
import { applyChromeConfig } from '../../_libs/site-shell/getChromeConfig';
import { usePortfolioQuoteBarDetail } from '../../_libs/site-shell/portfolioQuoteBar';
import RootHubNav from './RootHubNav';
import '../../styles/header.scss';

export interface RootHeaderProps {
  headerProps: NavigationHeaderProps & HeaderProps;
}

const isPortfolioPath = (pathname: string | null) =>
  Boolean(pathname && (pathname === '/profile/portfolio' || pathname.startsWith('/profile/portfolio/')));

const EMPTY_PORTFOLIO_TICKERS: string[] = [];

const RootHeader: React.FC<RootHeaderProps> = ({ headerProps }) => {
  const locale = usePageLocale();
  const pathname = usePathname();
  const { adLightEnabled } = useBenzingaEdge();
  const onPortfolio = isPortfolioPath(pathname);
  const chromeHeaderProps = useMemo(() => applyChromeConfig(pathname, headerProps), [headerProps, pathname]);
  const hideArticleHeaderAd = isArticlePath(pathname) && (adLightEnabled || isAdLightDomHintActive());
  const portfolioQuoteBar = usePortfolioQuoteBarDetail();

  useEffect(() => {
    document.addEventListener('om.Scripts.init', event => {
      if (event && event['detail']) {
        event['detail'].Scripts.enabled.fonts = false;
      }
    });

    disableOptinMonsterCampaigns();
  }, []);

  const visibleQuoteBar = onPortfolio && portfolioQuoteBar?.tickers?.length ? portfolioQuoteBar : null;
  const marketTickers = onPortfolio
    ? (visibleQuoteBar?.tickers ?? EMPTY_PORTFOLIO_TICKERS)
    : chromeHeaderProps?.marketTickers;
  const quotes = useMemo(() => {
    if (!onPortfolio) return chromeHeaderProps?.quotes;
    if (!visibleQuoteBar?.quotes) return null;
    return visibleQuoteBar.quotes;
  }, [chromeHeaderProps?.quotes, onPortfolio, visibleQuoteBar]);

  if (chromeHeaderProps?.hideNavigationBar) {
    return null;
  }

  if (chromeHeaderProps?.hubNavEnabled ?? resolveHubNavEnabled(locale)) {
    return <RootHubNav headerProps={{ ...chromeHeaderProps, quotes, marketTickers }} />;
  }

  return (
    <ErrorBoundary name="header">
      <NavigationHeader
        hideBanner={chromeHeaderProps?.hideBanner}
        hideMenuBar={chromeHeaderProps?.hideMenuBar}
        hideQuoteBar={chromeHeaderProps?.hideQuoteBar}
        hideSearchBar={chromeHeaderProps?.hideSearchBar}
        isMainDrawerVisible={chromeHeaderProps?.isMainDrawerVisible}
        initialBreakingsNews={chromeHeaderProps?.breakingNews}
        initialTrendingTickers={chromeHeaderProps?.initialTrendingTickers}
        initialTrendingTopics={chromeHeaderProps?.initialTrendingTopics}
        logoVariant={chromeHeaderProps?.navigationLogoVariant || 'default'}
        marketTickers={marketTickers}
        menus={chromeHeaderProps?.menus ?? menus}
        primaryQuotesLabel={onPortfolio ? 'Watchlist' : undefined}
        quotes={quotes}
        showAboveHeaderBlock={false}
        expandedHeaderDisplayAd={chromeHeaderProps?.expandedHeaderDisplayAd}
        showHeaderDisplayAd={chromeHeaderProps?.showHeaderDisplayAd ?? true}
        showRotatingBanner={
          hideArticleHeaderAd
            ? false
            : typeof chromeHeaderProps?.showRotatingBanner === 'boolean'
              ? chromeHeaderProps.showRotatingBanner
              : false
        }
        sticky={chromeHeaderProps?.sticky}
        variant={chromeHeaderProps?.variant ?? 'default'}
      />
    </ErrorBoundary>
  );
};

export default RootHeader;
